// src/reports/DiscountAnalyticsReport.jsx — Reporting → Store Reports → Backend Reporting → Discount Analytics
// (Eric, 2026-10-18). Replaces the Grafana "Lightspeed Discount Analytics Dashboard", from OpsHub's Lightspeed
// tables. An order is a completed, non-voided sale that took money; each discount dollar is credited to the discount
// that gave it (from the sale lines). Needs the Extra Permission reports.backend (finance + admin).
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Card, BlockStack, InlineStack, InlineGrid, Text, Banner, Button, Tabs, Spinner, Box, Tooltip } from '@shopify/polaris';
import { LineChart, BarChart } from '@shopify/polaris-viz';
import { useSearchParams } from 'react-router-dom';
import { useAuthFetch } from '../AuthComponent';
import { withColors, seriesColor } from './chartPalette';
import ColorDonut from './ColorDonut';
import ReportGuide from './ReportGuide';
import { ReportDateRange, ReportPicker, isoDaysAgo, todayIso, downloadCsv, paneHeightToFitScroller } from '../Reporting';

const fmtN = n => (n === null || n === undefined) ? '—' : Number(n).toLocaleString();
const fmtUSD = n => (n === null || n === undefined) ? '—' : '$' + Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtUSD0 = n => (n === null || n === undefined) ? '—' : '$' + Math.round(Number(n)).toLocaleString();
const fmtP = n => (n === null || n === undefined) ? '—' : Number(n).toFixed(1) + '%';

const TABS = [
    ['store', 'Penetration by store'], ['employee', 'Discount By Employee'], ['codes', 'Top discount codes'], ['codeStore', 'Codes by store'], ['detail', 'Transactions'],
    ['penetration', 'Penetration over time'], ['avgPct', 'Average discount % over time'], ['dollarsStore', 'Discount $ by store'],
    ['usage', 'Daily discount usage'], ['dollarsDay', 'Daily discount $'],
];
const COL_HELP = {
    orders: 'Orders: completed, non-voided sales that took money.',
    discounted: 'Orders with a discount: orders where a discount took money off.',
    penetration_pct: 'Discount rate = orders with a discount ÷ all orders.',
    dollars: 'Discount dollars given.',
    avg_pct: "Average % off: the discount rate on the items sold (discount ÷ price before discounts), averaged over discounted orders.",
    uses: 'Orders that used the discount. An order with two discounts counts once under each.',
    revenue: 'Net sales (tax excluded) on those orders.',
    avg_order: 'Net sales ÷ orders.',
};

function SortTable({ cols, rows, totalRow = null, note = null, fit = true }) {
    const [sort, setSort] = useState({ key: null, dir: 'desc' });
    const wrapRef = useRef(null);
    const [paneH, setPaneH] = useState(null);
    useLayoutEffect(() => {
        if (!fit) return undefined;
        const measure = () => { const el = wrapRef.current; if (!el) return; const h = paneHeightToFitScroller(el); setPaneH(prev => (prev != null && Math.abs(prev - h) <= 1) ? prev : h); };
        measure();
        const t = setTimeout(measure, 300);
        window.addEventListener('resize', measure);
        return () => { clearTimeout(t); window.removeEventListener('resize', measure); };
    }, [fit, rows, paneH]);
    const sorted = useMemo(() => {
        if (!sort.key) return rows;
        const k = sort.key, d = sort.dir === 'asc' ? 1 : -1;
        return [...rows].sort((a, b) => {
            const x = a[k], y = b[k];
            if (x === y) return 0;
            if (x === null || x === undefined || x === '') return 1;
            if (y === null || y === undefined || y === '') return -1;
            return (typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y))) * d;
        });
    }, [rows, sort]);
    const cell = (r, k, kind) => {
        const v = r[k];
        if (kind === 'n') return fmtN(v);
        if (kind === 'u') return fmtUSD(v);
        if (kind === 'p') return fmtP(v);
        if (kind === 'l') return v ? <a href={v} target="_blank" rel="noreferrer">View in Lightspeed</a> : '';
        return v ?? '';
    };
    const td = (kind, extra = {}) => ({ textAlign: ['s', 'l', 'w'].includes(kind) ? 'left' : 'right', padding: '5px 8px', borderBottom: '1px solid var(--p-color-border-subdued, #eee)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', maxWidth: kind === 'w' ? 340 : undefined, overflow: kind === 'w' ? 'hidden' : undefined, textOverflow: kind === 'w' ? 'ellipsis' : undefined, ...extra });
    return (
        <BlockStack gap="200">
            {note && <Text as="p" variant="bodySm" tone="subdued">{note}</Text>}
            <div ref={wrapRef} style={{ overflow: 'auto', maxHeight: fit ? (paneH || undefined) : 460 }}>
                <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
                    <thead style={{ position: 'sticky', top: 0, background: 'var(--p-color-bg-surface, #fff)', zIndex: 1 }}>
                        <tr>{cols.map(([k, label, kind, help]) => (
                            <th key={k} onClick={() => setSort(s => ({ key: k, dir: s.key === k && s.dir === 'desc' ? 'asc' : 'desc' }))}
                                style={{ textAlign: ['s', 'l', 'w'].includes(kind) ? 'left' : 'right', padding: '6px 8px', cursor: 'pointer', whiteSpace: 'nowrap', borderBottom: '1px solid var(--p-color-border, #ddd)' }}>
                                <Tooltip content={help || COL_HELP[k] || label}><span style={{ borderBottom: '1px dotted currentColor' }}>{label}</span></Tooltip>{sort.key === k ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                            </th>))}</tr>
                    </thead>
                    <tbody>
                        {sorted.map((r, i) => (<tr key={i}>{cols.map(([k, , kind]) => <td key={k} style={td(kind)} title={kind === 'w' ? String(r[k] ?? '') : undefined}>{cell(r, k, kind)}</td>)}</tr>))}
                        {totalRow && <tr>{cols.map(([k, , kind]) => <td key={k} style={td(kind, { fontWeight: 600, borderTop: '2px solid var(--p-color-border, #ccc)' })}>{cell(totalRow, k, kind)}</td>)}</tr>}
                    </tbody>
                </table>
                {rows.length === 0 && <Box padding="400"><Text as="p" tone="subdued" alignment="center">No discounts in these dates.</Text></Box>}
            </div>
        </BlockStack>
    );
}

function Stat({ label, value, hint }) {
    const body = (
        <Box padding="400" background="bg-surface-secondary" borderRadius="200">
            <BlockStack gap="100"><Text as="p" variant="bodySm" tone="subdued">{label}</Text><Text as="p" variant="heading2xl">{value}</Text></BlockStack>
        </Box>);
    return hint ? <Tooltip content={hint}>{body}</Tooltip> : body;
}

// {date, store, value} rows -> one series per store over the dates (0 where the store had none, for money/counts)
function lines(daily, dates, pick, zero) {
    const by = {}, tot = {};
    daily.forEach(r => { (by[r.store] = by[r.store] || {})[r.date] = pick(r); tot[r.store] = (tot[r.store] || 0) + (r.dollars || 0); });
    return withColors(Object.keys(by).sort((a, b) => tot[b] - tot[a]).map(st => ({
        name: st, data: dates.map(d => ({ key: d, value: by[st][d] !== undefined && by[st][d] !== null ? by[st][d] : (zero ? 0 : null) })),
    })));
}

const B = ({ children }) => <b>{children}</b>;
const GUIDE = {
    summary: 'How often and how deeply the stores discount: by store, employee and discount, over any dates.',
    sections: [
        { heading: 'What it is for', items: [
            'See how many orders are discounted, how many dollars are given away and at what average percentage.',
            'Compare stores and employees on how often they discount, and see which discounts are used most.',
            'Tell member discounts (Partner Club, Member) apart from promotional codes.',
        ] },
        { heading: 'Where the numbers come from', items: [
            "OpsHub's own copy of Lightspeed (our point-of-sale system): sales, the discount on each sale line, the discount names (including retired promotions), employees and stores.",
            'The copy is refreshed every night (1:15 AM), so the report is complete through last night, not today.',
            'Dates are the store\'s own local date.',
        ] },
        { heading: 'Key terms', items: [
            <><B>Order</B>: a completed, non-voided sale that took money. $0 tickets (mostly gift cards being loaded) and returns are not orders.</>,
            <><B>Discounted order</B>: an order where a discount took money off. <B>Discount rate / penetration</B>: discounted orders divided by all orders.</>,
            <><B>Discount $</B>: the dollars taken off. It is netted per sale: in an exchange, the discount on the returned item reverses against the discount on the new one, so you see what was really given.</>,
            <><B>Average discount %</B>: the discount rate on the items sold (discount divided by their price before discounts), averaged across discounted orders. An exchange of a 40%-off jersey for another 40%-off jersey reads 40%.</>,
            <><B>Member vs promo</B>: a discount named with "Partner Club" or "Member" is a member discount; anything else is a promotional code.</>,
        ] },
        { heading: 'How dollars are credited', items: [
            'Each discount dollar is credited to the discount that gave it, taken from the sale lines. An order that used two discounts counts once under each, and once in the totals.',
            'A whole-ticket discount that is not tied to a line is credited to that discount, or shown as "(transaction discount)".',
            'Net revenue (in the code tables) is the sale total minus sales tax.',
        ] },
        { heading: 'How to use it', items: [
            'Pick dates, stores and a discount (blank = all), then Run Report. The address in your browser holds your choices.',
            'The four boxes are the headline numbers; the donut and bars show member-vs-promo and how deep orders are discounted.',
            'Tabs: penetration by store, Discount By Employee, top discount codes, codes by store, the transaction list (with a link to each sale in Lightspeed), and charts over time.',
            'Click a column title to sort; hover for its meaning; Export CSV saves the table you are viewing.',
        ] },
    ],
};

export default function DiscountAnalyticsReport() {
    const fetchAuth = useAuthFetch();
    const [sp, setSp] = useSearchParams();
    const [dateFrom, setDateFrom] = useState(sp.get('from') || isoDaysAgo(6));
    const [dateTo, setDateTo] = useState(sp.get('to') || todayIso());
    const [stores, setStores] = useState(sp.getAll('stores'));
    const [codes, setCodes] = useState(sp.getAll('codes'));
    const [tab, setTabState] = useState(Math.max(0, TABS.findIndex(t => t[0] === sp.get('tab'))));
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    useEffect(() => {
        document.body.classList.add('report-flush-bottom');
        return () => document.body.classList.remove('report-flush-bottom');
    }, []);

    const writeUrl = useCallback((f, tabIdx) => {
        setSp(prev => {
            const n = new URLSearchParams(prev);
            ['from', 'to', 'stores', 'codes', 'tab'].forEach(k => n.delete(k));
            n.set('from', f.dateFrom); n.set('to', f.dateTo);
            f.stores.forEach(v => n.append('stores', v)); f.codes.forEach(v => n.append('codes', v));
            if (tabIdx > 0) n.set('tab', TABS[tabIdx][0]);
            return n;
        }, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setSp]);
    const setTab = (i) => { setTabState(i); writeUrl({ dateFrom, dateTo, stores, codes }, i); };

    const run = useCallback(async () => {
        setBusy(true); setErr(null);
        writeUrl({ dateFrom, dateTo, stores, codes }, tab);
        try {
            const p = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
            stores.forEach(v => p.append('stores', v)); codes.forEach(v => p.append('codes', v));
            const r = await fetchAuth(`/api/reports/backend/discount-analytics?${p}`);
            if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`);
            setData(await r.json());
        } catch (e) { setErr(String(e.message || e)); } finally { setBusy(false); }
    }, [fetchAuth, dateFrom, dateTo, stores, codes, tab, writeUrl]);
    useEffect(() => { run(); /* eslint-disable-next-line */ }, []);

    // the store list stays whole when only a code is picked; the code list follows the stores in view
    const storeOptions = useMemo(() => (data?.store_options || []).map(v => ({ value: v, label: v })), [data]);
    const codeOptions = useMemo(() => (data?.code_options || []).map(c => ({ value: c.name, label: `${c.name} (${fmtN(c.orders)})` })), [data]);
    const dates = useMemo(() => {
        if (!data) return [];
        const out = [];
        for (let d = new Date(`${data.date_from}T00:00:00Z`); d <= new Date(`${data.date_to}T00:00:00Z`); d.setUTCDate(d.getUTCDate() + 1)) out.push(d.toISOString().slice(0, 10));
        return out;
    }, [data]);
    const daily = useMemo(() => data?.daily || [], [data]);
    const series = useMemo(() => ({
        penetration: lines(daily, dates, r => r.orders ? Math.round(r.discounted / r.orders * 1000) / 10 : null, true),
        avgPct: lines(daily, dates, r => r.avg_pct === null || r.avg_pct === undefined ? null : Math.round(r.avg_pct * 10) / 10, false),
        usage: lines(daily, dates, r => r.discounted, true),
        dollarsDay: lines(daily, dates, r => Math.round(r.dollars * 100) / 100, true),
    }), [daily, dates]);
    const donutStore = useMemo(() => (data?.by_store || []).filter(r => r.dollars > 0).sort((a, b) => b.dollars - a.dollars).map((r, i) => ({ name: r.store, color: seriesColor(i), data: [{ key: 'dollars', value: r.dollars }] })), [data]);
    const donutKinds = useMemo(() => (data?.kinds || []).map((r, i) => ({ name: r.type, color: seriesColor(i), data: [{ key: 'orders', value: r.orders }] })), [data]);
    const rangeBars = useMemo(() => [{ name: 'Orders', data: (data?.ranges || []).map(r => ({ key: r.bucket, value: r.orders })) }], [data]);

    const S = data?.summary;
    const key = TABS[tab][0];
    const COLS = {
        store: [['store', 'Store', 's'], ['orders', 'Total orders', 'n'], ['discounted', 'Orders with discount', 'n'], ['penetration_pct', 'Discount rate %', 'p'], ['dollars', 'Total discount $', 'u'], ['avg_pct', 'Avg discount %', 'p']],
        employee: [['employee', 'Employee', 's'], ['stores', 'Stores', 'w'], ['orders', 'Total orders', 'n'], ['discounted', 'Orders with discount', 'n'], ['penetration_pct', '% of orders discounted', 'p'],
        ['member_orders', 'Member discount orders', 'n', 'Orders that used a member discount (Partner Club / Member).'], ['promo_orders', 'Promo code orders', 'n', 'Orders that used a promotional discount. An order with both counts in both columns.'],
        ['dollars', 'Total discount $', 'u'], ['member_dollars', 'Member discount $', 'u'], ['promo_dollars', 'Promo discount $', 'u'], ['avg_pct', 'Avg discount %', 'p']],
    codes: [['code', 'Discount code', 'w'], ['uses', 'Usage count', 'n'], ['dollars', 'Total discount amount', 'u'], ['avg_pct', 'Avg discount %', 'p'], ['revenue', 'Net revenue', 'u'], ['avg_order', 'Avg order value', 'u'], ['stores', 'Stores used', 'w']],
        codeStore: [['store', 'Store', 's'], ['code', 'Discount code', 'w'], ['uses', 'Total uses', 'n'], ['dollars', 'Total discount $', 'u'], ['avg_pct', 'Avg %', 'p'], ['first_used', 'First used', 's'], ['last_used', 'Last used', 's']],
        detail: [['sale_id', 'Transaction ID', 's'], ['time', 'Transaction date', 's'], ['store', 'Store', 's'], ['employee', 'Employee', 's'], ['customer', 'Customer', 's'], ['codes', 'Discount codes', 'w'], ['type', 'Type', 's'], ['discount', 'Discount amount', 'u'], ['pct', 'Discount %', 'p'], ['net', 'Net sale', 'u'], ['link', 'View in Lightspeed', 'l']],
    };
    const rowsFor = { store: data?.by_store, employee: data?.by_employee, codes: data?.by_code, codeStore: data?.code_by_store, detail: data?.detail };
    const ct = data?.code_total;
    const totals = {
        codes: ct ? { code: 'TOTAL / AVERAGE', uses: ct.uses, dollars: ct.dollars, avg_pct: ct.avg_pct, revenue: ct.revenue, avg_order: ct.avg_order, stores: '' } : null,
        codeStore: ct ? { store: 'TOTAL / AVERAGE', code: '', uses: ct.uses, dollars: ct.dollars, avg_pct: ct.avg_pct, first_used: '', last_used: '' } : null,
    };
    const exportCsv = () => {
        if (!data || !COLS[key]) return;
        downloadCsv(`discount-analytics-${key}-${dateFrom}-${dateTo}.csv`, [COLS[key].map(c => c[1]), ...(rowsFor[key] || []).map(r => COLS[key].map(c => r[c[0]] ?? ''))]);
    };
    const moneyAxis = { yAxisOptions: { labelFormatter: v => '$' + Number(v).toLocaleString() }, tooltipOptions: { valueFormatter: fmtUSD } };
    const pctAxis = { yAxisOptions: { labelFormatter: v => `${v}%` }, tooltipOptions: { valueFormatter: v => `${Number(v).toFixed(1)}%` } };

    return (
        <BlockStack gap="300">
            {err && <Banner tone="critical" onDismiss={() => setErr(null)}>{err}</Banner>}
            <ReportGuide id="discount-analytics" summary={GUIDE.summary} sections={GUIDE.sections} />
            <Card>
                <BlockStack gap="300">
                    <InlineStack gap="200" blockAlign="center" wrap>
                        <ReportDateRange dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo} />
                        <ReportPicker label="Store" options={storeOptions} value={stores} onChange={setStores} multiple searchable placeholder="All stores" />
                        <ReportPicker label="Discount" options={codeOptions} value={codes} onChange={setCodes} multiple searchable placeholder="All discounts" minWidth={380} />
                        <Button variant="primary" onClick={run} loading={busy}>Run Report</Button>
                        <Button onClick={exportCsv} disabled={!data || !COLS[key]}>Export CSV</Button>
                    </InlineStack>
                    <Text as="p" variant="bodySm" tone="subdued">
                        An order is a completed, non-voided Lightspeed sale that took money, by the store's local date. Each discount dollar is credited to the discount that gave it,
                        so an order with two discounts counts once under each (and once in the totals). Discounts are netted per sale (an exchange's returned item reverses its discount); % off is the discount rate on the items sold.
                        {data?.synced_through ? ` Sales load nightly, through ${String(data.synced_through).slice(0, 16).replace('T', ' ')}.` : ''}
                    </Text>
                </BlockStack>
            </Card>

            {busy && !data && <InlineStack align="center"><Spinner /></InlineStack>}
            {S && (
                <InlineGrid columns={{ xs: 1, sm: 2, lg: 4 }} gap="200">
                    <Stat label="Total discounts given" value={fmtUSD(S.dollars)} hint="Discount dollars on orders in the dates." />
                    <Stat label="Orders with discounts" value={fmtN(S.discounted)} hint="Orders where a discount took money off." />
                    <Stat label="Average discount %" value={S.avg_pct === null || S.avg_pct === undefined ? '—' : fmtP(S.avg_pct)} hint="Average % off on the items sold, across the discounted orders." />
                    <Stat label="Overall discount penetration rate" value={fmtP(S.penetration_pct)} hint="Orders with a discount ÷ all orders." />
                </InlineGrid>
            )}

            {data && (
                <InlineGrid columns={{ xs: 1, md: 2 }} gap="300">
                    <Card>
                        <BlockStack gap="200">
                            <Text as="h3" variant="headingSm">Discount type distribution (member vs promo)</Text>
                            <ColorDonut data={donutKinds} format={fmtN} height={260} />
                        </BlockStack>
                    </Card>
                    <Card>
                        <BlockStack gap="200">
                            <Text as="h3" variant="headingSm">Order distribution by discount range</Text>
                            <div style={{ height: 260 }}><BarChart data={rangeBars} /></div>
                        </BlockStack>
                    </Card>
                </InlineGrid>
            )}

            {data && (
                <Card padding="0">
                    <Tabs tabs={TABS.map(([id, content]) => ({ id, content }))} selected={tab} onSelect={setTab} />
                    <Box padding="300" paddingBlockEnd="0">
                        {COLS[key] && (
                            <SortTable key={key} cols={COLS[key]} rows={rowsFor[key] || []} totalRow={totals[key] || null}
                                note={key === 'detail' && data.detail_total > data.detail_limit ? `Showing the latest ${fmtN(data.detail_limit)} of ${fmtN(data.detail_total)} discounted orders — narrow the dates or filters.` : null} />
                        )}
                        {key === 'penetration' && <div style={{ height: 420 }}><LineChart data={series.penetration} {...pctAxis} /></div>}
                        {key === 'avgPct' && <div style={{ height: 420 }}><LineChart data={series.avgPct} {...pctAxis} /></div>}
                        {key === 'dollarsStore' && <div style={{ height: 420 }}><ColorDonut data={donutStore} format={fmtUSD} height={420} /></div>}
                        {key === 'usage' && <div style={{ height: 420 }}><LineChart data={series.usage} /></div>}
                        {key === 'dollarsDay' && <div style={{ height: 420 }}><LineChart data={series.dollarsDay} {...moneyAxis} /></div>}
                    </Box>
                </Card>
            )}
        </BlockStack>
    );
}
